import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-contrib-quality-levels";
import "videojs-hls-quality-selector";
import "videojs-http-source-selector";
import styles from "./PlayerMain.module.scss";
import Hls from "hls.js";
import { SubtitleItem } from "@/shared/types/player";
import { useSubtitles } from "@/pages/PlayerPage/lib/useSubtitles";
import { usePlayerStore } from "@/features/player/lib/usePlayerStore";
import VjsDualVolume from "@/pages/PlayerPage/ui/VjsDualVolume";
import { classNames } from "@/shared/lib/classNames/classNames";
import VjsSettings from "./VjsSettings";

interface VideoPLayerProps {
    audioRef: React.RefObject<HTMLAudioElement | null>;
    onPlay: () => void;
    onPause: () => void;
    audioFile: string | null;
    subtitles: SubtitleItem[];
    videoVolume: number;
    selectLang: string;
    setSelectLang: (val: string) => void;
    onAudioVolumeChange?: any;
}

const videoSrc =
    "https://fastapi.dev.torsunov.pro/download_rus?file_name=01ba5e1e28b1449bbc71d7ef98a3fed0/playlists/vod.m3u8";

export const VideoPlayer = ({
    audioRef,
    onPlay,
    onPause,
    audioFile,
    subtitles,
    videoVolume,
    selectLang,
    setSelectLang,
    onAudioVolumeChange,
}: VideoPLayerProps) => {
    const store = usePlayerStore();
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<any>(null);

    const [isVideoCanPlay, setIsVideoCanPlay] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);

    const { activeCue, addJsonSubtitlesToPlayer, handleClickSubtitle } =
        useSubtitles({
            subtitles,
            lang: selectLang,
            videoRef: videoRef.current,
        });
    console.log("selectLang player", selectLang);
    // Загружаем HLS-поток
    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        let hls: Hls;

        if (videoRef.current && Hls.isSupported()) {
            hls = new Hls();
            hls.loadSource(videoSrc);
            hls.attachMedia(videoRef.current);
        } else if (videoRef.current) {
            videoRef.current.src = videoSrc; // fallback для Safari
        }
        if (!playerRef.current) {
            playerRef.current = videojs(videoRef.current, {
                controls: true,
                autoplay: false,
                preload: "auto",
                controlBar: {
                    volumePanel: false,
                },
            });
        }

        playerRef.current.ready(() => {
            if (!playerRef.current) return;
            const controlBar = playerRef.current.getChild("controlBar");

            if (controlBar) {
                controlBar.addChild(
                    new VjsDualVolume(playerRef.current, {
                        audioRef,
                        videoRef,
                    }),
                    {},
                    1,
                );

                controlBar.addChild(
                    new VjsSettings(playerRef.current, {
                        audioRef,
                        videoRef,
                        selectLang,
                        setSelectLang,
                    }),
                    {},
                    controlBar.children().length - 1,
                );
            }
        });

        playerRef.current.on("fullscreenchange", () => {
            if (playerRef.current?.isFullscreen()) {
                console.log("Entered fullscreen");
                setIsFullScreen(true);
            } else {
                setIsFullScreen(false);
                console.log("Exited fullscreen");
            }
        });

        return () => hls?.destroy();
    }, [audioRef]);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.volume = videoVolume;
        }
    }, [videoVolume]);

    useEffect(() => {
        if (playerRef.current) {
            addJsonSubtitlesToPlayer(playerRef.current);
        }
    }, [addJsonSubtitlesToPlayer, subtitles]);

    // Синхронизация аудио и видео
    useEffect(() => {
        const videoEl = videoRef.current;
        const audioEl = audioRef?.current;
        if (!videoEl || !audioEl) return;

        const sync = () => {
            if (Math.abs(audioEl.currentTime - videoEl.currentTime) > 0.3) {
                if (audioEl) {
                    audioEl.currentTime = videoEl.currentTime;
                }
            }
        };

        videoEl.addEventListener("timeupdate", sync);
        return () => {
            videoEl.removeEventListener("timeupdate", sync);
        };
    }, [audioRef, audioFile]);

    const handleCanPlay = () => {
        setIsVideoCanPlay(true);
    };

    const handleCueClick = () => {
        handleClickSubtitle();
    };

    return (
        <div
            className={classNames(styles.videoWrapper, {
                [styles.notFullScreen]: !isFullScreen,
            })}
        >
            <div data-vjs-player>
                <video
                    ref={videoRef}
                    className="video-js vjs-default-skin"
                    playsInline
                    onPlay={onPlay}
                    onPause={onPause}
                    onWaiting={() => console.log("onWaiting")}
                    onCanPlay={handleCanPlay}
                    controls
                />

                <div
                    className={styles.customSubtitle}
                    onClick={handleCueClick}
                />
            </div>
        </div>
    );
};
